import { useState } from "react";
import { Link } from "react-router";
import { ActivityFeed } from "../components/ActivityFeed.tsx";
import { NewProjectDialog, NewSessionDialog } from "../components/Dialogs.tsx";
import { PromptCard } from "../components/PromptCard.tsx";
import { Empty, ErrorBox, Loading, useNow } from "../components/ui.tsx";
import { useApi } from "../lib/api.ts";
import { ago, duration, STATE_LABELS, STATE_TONE } from "../lib/format.ts";
import type { Overview as OverviewData, Project, SessionState, Settings } from "../lib/types.ts";

export function StateCount({ state, n }: { state: SessionState; n: number }) {
  const tone = STATE_TONE[state];
  return (
    <span className={`pill ${tone === "muted" ? "" : tone}`}>
      <i className={`dot ${tone === "muted" ? "" : tone}`} />
      {n} {STATE_LABELS[state].toLowerCase()}
    </span>
  );
}

export function channelUrl(guildId: unknown, channelId: string | null): string | null {
  return typeof guildId === "string" && guildId && channelId ? `https://discord.com/channels/${guildId}/${channelId}` : null;
}

export function Overview() {
  const overview = useApi<OverviewData>("/api/overview");
  const settings = useApi<Settings>("/api/settings");
  const [dialog, setDialog] = useState<null | { kind: "project" } | { kind: "session"; project?: Project }>(null);
  const [openPrompt, setOpenPrompt] = useState<string | null>(null);
  const now = useNow(30_000);

  if (!overview.data) return overview.error ? <ErrorBox error={overview.error} retry={() => overview.mutate()} /> : <Loading />;
  const o = overview.data;
  const c = o.counts;
  const running = c.working + c.rate_limited;
  const idle = c.idle + c.done + c.queued + c.created;
  const guild = settings.data?.["discord.guild_id"];

  return (
    <>
      <div className="pagehead">
        <h1>Overview</h1>
        <div className="row">
          <button className="btn" onClick={() => setDialog({ kind: "project" })}>
            New project
          </button>
          <button className="btn primary" disabled={!o.projects.length} onClick={() => setDialog({ kind: "session" })}>
            New session
          </button>
        </div>
      </div>

      <div className="tiles">
        <Link to="/sessions?state=waiting" className={`tile ${o.pending.length ? "warn" : ""}`}>
          <span className="n">{o.pending.length}</span>
          <span className="l">Waiting on you</span>
        </Link>
        <Link to="/sessions?state=working" className="tile">
          <span className="n">{running}</span>
          <span className="l">Running</span>
        </Link>
        <Link to="/sessions?state=idle" className="tile">
          <span className="n">{idle}</span>
          <span className="l">Idle</span>
        </Link>
        <Link to="/sessions?state=errored" className={`tile ${c.errored ? "bad" : ""}`}>
          <span className="n">{c.errored}</span>
          <span className="l">Errored</span>
        </Link>
      </div>

      {o.pending.length > 0 && (
        <section className="needs" aria-label="Waiting on you">
          <header>
            <i className="dot warn pulse" /> {o.pending.length} waiting on you
          </header>
          {o.pending.map((p) => {
            const open = openPrompt === p.id || (openPrompt === null && p === o.pending[0]);
            const what = p.kind === "question" ? `is asking: “${p.options?.[0]?.question ?? "a question"}”` : `needs permission · ${p.toolName}`;
            return (
              <div key={p.id} className="item">
                <div className="row between">
                  <span>
                    <Link to={`/sessions/${p.session.id}`}>
                      <b>{p.session.name}</b>
                    </Link>
                    {p.project && <span className="muted"> ({p.project.slug})</span>} {what}
                    <span className="muted small"> · {ago(p.createdAt, now)}</span>
                  </span>
                  <button className="btn sm" onClick={() => setOpenPrompt(open ? "" : p.id)}>
                    {open ? "Hide" : "Answer"}
                  </button>
                </div>
                {open && <PromptCard prompt={p} />}
              </div>
            );
          })}
        </section>
      )}

      <div className="split">
        <div className="stack">
          {o.projects.length === 0 ? (
            <div className="card">
              <Empty title="No projects yet">
                <span>A project is a folder under your projects root, bound to a Discord channel.</span>
                <button className="btn primary" onClick={() => setDialog({ kind: "project" })}>
                  Create a project
                </button>
              </Empty>
            </div>
          ) : (
            o.projects.map((p) => {
              const states = (Object.entries(p.states) as [SessionState, number][]).filter(([, n]) => n > 0);
              const ch = channelUrl(guild, p.channelId);
              const last = p.lastResult;
              const lp = last?.payload as { durationMs?: number; text?: string } | undefined;
              return (
                <section key={p.id} className="card">
                  <header>
                    <div>
                      <h2>
                        <Link to={`/projects/${p.slug}`} style={{ color: "inherit" }}>
                          {p.name}
                        </Link>
                      </h2>
                      <div className="muted small mono">
                        {p.channelId ? `#${p.slug}` : "no channel yet"} · {p.rootPath}
                      </div>
                    </div>
                    {p.status === "archived" && <span className="pill">archived</span>}
                  </header>
                  <div className="row">
                    {states.length ? states.map(([s, n]) => <StateCount key={s} state={s} n={n} />) : <span className="muted small">No sessions</span>}
                  </div>
                  {last && (
                    <div className="small">
                      <span className="muted">Last change </span>
                      <Link to={`/sessions/${last.sessionId}`}>{last.sessionName}</Link>
                      <span className="muted">
                        {" "}
                        · {ago(last.createdAt, now)}
                        {lp?.durationMs ? ` · ${duration(lp.durationMs)}` : ""}
                      </span>
                      {lp?.text && <div className="muted ellipsis">{lp.text.split("\n")[0]}</div>}
                    </div>
                  )}
                  <div className="row">
                    <Link className="btn sm" to={`/projects/${p.slug}`}>
                      Sessions ({p.sessionCount})
                    </Link>
                    <button className="btn sm" disabled={p.status !== "active"} onClick={() => setDialog({ kind: "session", project: p })}>
                      New session
                    </button>
                    {ch && (
                      <a className="btn sm ghost" href={ch} target="_blank" rel="noreferrer">
                        Open channel ↗
                      </a>
                    )}
                  </div>
                </section>
              );
            })
          )}
        </div>
        <section className="card">
          <header>
            <h2>Activity</h2>
            <Link to="/activity" className="small">
              All activity
            </Link>
          </header>
          <ActivityFeed items={o.activity} />
        </section>
      </div>

      {dialog?.kind === "project" && <NewProjectDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === "session" && <NewSessionDialog project={dialog.project} onClose={() => setDialog(null)} />}
    </>
  );
}
